<template>
  <div :key="activeInstitutionId" :style="settingsStore.colorSetup" class="webkit-fill-fix">
    <template v-if="amILoggedIn">
      <div class="flex flex-col h-screen border-box">
        <TopPanel class="absolute inset-x-0 top-0 h-16 md:h-24" />

        <div class="flex-grow flex pt-16 md:pt-24 webkit-fill-fix">
          <LeftPanel />
          <main v-if="!isLoading" id="main-content" class="flex-grow p-4 md:p-8 overflow-auto pb-20 md:pb-4">
            <router-view />
          </main>
        </div>
      </div>
      <transition name="fade">
        <div v-if="isLoading" id="preloader" class="flex items-center justify-center">
          <InfoSpinner></InfoSpinner>
        </div>
      </transition>
      <GlobalSnackbar></GlobalSnackbar>
    </template>

    <template v-if="!amILoggedIn">
      <div class="flex h-screen border-box justify-center items-center bg-gray-100">
        <div class="bg-white border-gray-200 border w-1/2 text-center p-12 rounded-lg">
          <h2 class="text-20 text-black-60% font-semibold">
            {{ customerSystemName ? $t("Home.YouHaveBeenLoggedOutOf") : $t("Home.YouHaveBeenLoggedOut") }}
            {{ customerSystemName }}
          </h2>

          <img v-if="customerSystemLogo" :src="customerSystemLogo" class="mt-8 mx-auto h-20" />

          <div class="text-sm mt-8">
            <a :href="kvikloginUrl" class="underline">{{ $t("Home.LoginAgain") }}</a>
          </div>
        </div>

        <img src="https://assets.nordiccloud.dk/logos/nct/NordicCloudTransparent.png" class="fixed bottom-14px w-40" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import apiSkobo from "@/helpers/api/apiSkobo";
import msalConnection from "@/helpers/api/msalConnection";

import { useSettingsStore } from "@/stores/settings";
import { useInstitutionsStore } from "@/stores/institutions";
import { useColorSetup } from "@/composables/useColorSetup";
import { CookieHelper } from "@/helpers/utilities/CookieHelper";
import { SessionHelper } from "@/helpers/utilities/sessionHelper";

import LeftPanel from "@/components/Navigation/LeftPanel";
import TopPanel from "@/components/Navigation/TopPanel";
import GlobalSnackbar from "@/components/Shared/GlobalSnackbar.vue";
import InfoSpinner from "@/components/Shared/InfoSpinner.vue";

defineOptions({ name: "NwpApp" });

const router = useRouter();
const settingsStore = useSettingsStore();
const institutionsStore = useInstitutionsStore();

useColorSetup(computed(() => settingsStore.colorSetup));

const isLoading = ref(true);
const amILoggedIn = ref(!!SessionHelper.nctSessionToken());

const activeInstitutionId = computed(() => institutionsStore.myActiveInstitutionId || "noinst");

const kvikloginUrl = computed(() =>
  import.meta.env.VITE_APP_NCT_LOGIN_ENDPOINT ? import.meta.env.VITE_APP_NCT_LOGIN_ENDPOINT : "https://kviklogin.dk"
);

const customerSystemName = computed(() => SessionHelper.customerSystemName);
const customerSystemLogo = computed(() => SessionHelper.customerSystemLogo);

const loadApplicationSetup = async () => {
  const result = await apiSkobo.get("/me/setup");
  const setupObject = result.data;
  await settingsStore.loadSetup(setupObject);
};

const setInitialInstitution = async () => {
  await institutionsStore.setActiveInstitutionFromToken();
};

const setupMSAL = async () => {
  await msalConnection.init(settingsStore.interfaces.tenant, settingsStore.interfaces.graphAPIClientId);
};

const checkForRedirectPath = () => {
  const pathForLoad = CookieHelper.getCookieForNextBrowserLoad();
  if (pathForLoad) {
    CookieHelper.deleteCookieForNextBrowserLoad();
    router.push({ path: pathForLoad });
  }
};

onMounted(async () => {
  if (amILoggedIn.value) {
    isLoading.value = true;

    await loadApplicationSetup();
    await setInitialInstitution();
    await setupMSAL();

    checkForRedirectPath();
    isLoading.value = false;

    SessionHelper.onLogout(() => {
      amILoggedIn.value = false;
    });
  }
});
</script>
